import { ReactNode, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import styled, { css } from "styled-components";

// Shared visual language for every full-page (non-map) route: Country,
// Learn (index + topic detail) and Autotest all render inside this same
// gradient/glass shell so the app feels like one cohesive product instead of
// a map with bolted-on subpages.

export const scrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: rgba(173, 216, 230, 0.55) rgba(24, 40, 72, 0.18);

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(24, 40, 72, 0.18);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px rgba(245, 226, 200, 0.08);
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      180deg,
      rgba(173, 216, 230, 0.68),
      rgba(173, 216, 230, 0.38)
    );
    border-radius: 999px;
    border: 2px solid rgba(24, 40, 72, 0.35);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(
      180deg,
      rgba(173, 216, 230, 0.85),
      rgba(173, 216, 230, 0.5)
    );
  }
`;

const PageSurface = styled.div`
  width: 100%;
  height: 100vh;
  background: linear-gradient(0deg, #182848, #2980b9);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  scrollbar-gutter: stable;
  ${scrollbar}

  @supports (overflow: clip) {
    overflow-x: clip;
  }
`;

// The page scrolls inside this element, not the window, so the router's own
// <ScrollRestoration /> — which only knows about the window — restores
// nothing. Going back from an article to the Learn index used to land at the
// top of a long list, with no trace of where you had been reading.
//
// Positions are kept per history entry, so forward and back both land where
// they should, and an entry reached by a fresh navigation starts at the top
// the way a new page should.
const scrollPositions = new Map<string, number>();

export const Page = ({
  children,
  ...rest
}: {
  children: ReactNode;
  id?: string;
  lang?: string;
}) => {
  const surface = useRef<HTMLDivElement>(null);
  const { key } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    const node = surface.current;
    if (!node) return;

    // POP is the back and forward buttons; anything else is a new arrival.
    const saved = navigationType === "POP" ? scrollPositions.get(key) : undefined;
    node.scrollTop = saved ?? 0;

    const remember = () => scrollPositions.set(key, node.scrollTop);
    node.addEventListener("scroll", remember, { passive: true });
    return () => {
      // Also on the way out: the last scroll event may predate the click.
      remember();
      node.removeEventListener("scroll", remember);
    };
  }, [key, navigationType]);

  return (
    <PageSurface ref={surface} {...rest}>
      {children}
    </PageSurface>
  );
};

export const Content = styled.main`
  position: relative;
  z-index: 2;
  margin: 0 auto;
  max-width: 1120px;
  padding: 28px 20px 48px;
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 18px;
`;

export const BackButton = styled.button`
  cursor: pointer;
  border: 1px solid rgba(245, 226, 200, 0.35);
  color: #f5e2c8;
  background: rgba(27, 38, 79, 0.24);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 10px 12px;
  border-radius: 14px;
  letter-spacing: 0.2px;
  font-family: "Finger Paint", cursive;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.34);
    border-color: rgba(245, 226, 200, 0.6);
  }

  &:active {
    transform: translateY(0px);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

export const Card = styled.section`
  border-radius: 18px;
  border: 1px solid rgba(245, 226, 200, 0.16);
  background: rgba(27, 38, 79, 0.18);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.22);
  padding: 16px 16px 14px;
  overflow: hidden;
`;

export const CardTitle = styled.h2`
  margin: 0 0 10px;
  font-family: "Gaegu", cursive;
  font-size: 1.9rem;
  letter-spacing: 0.4px;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
